/**
 * MarketingApp — Marketing hub for Jubly.
 * Brand, templates, sites, campaigns, booking.
 * API calls go to Imagia at /api/jubly-marketing/*.
 * Contributor: ben@hapi.vc
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Palette, FileText, Globe, Mail, Calendar, ArrowLeft, Plus, Trash2, ExternalLink, Copy } from 'lucide-react';
import { BrandEditor, BrandApiClient } from '../brand-editor/index.js';
import { useAuth } from '../hooks/useAuth';

const API_BASE = 'https://imagia.net/api/jubly-marketing';
const PIKTO_API = 'https://pikto.us/api/partner';
const PARTNER_KEY = 'jb_partner_096544c1b8b63a01d8491d36b9206b051ac81eae92f65b83';

function makeApiFetch(userId) {
  return async function apiFetch(path, opts = {}) {
    const res = await fetch(API_BASE + path, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        ...(userId ? { 'X-User-Id': userId } : {}),
        ...opts.headers,
      },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });
    return res.json();
  };
}

const TABS = [
  // Hidden until the brand backend (AI brand builder) is built — see BrandTab below.
  // { key: 'brand', label: 'Brand', icon: Palette },
  { key: 'templates', label: 'Templates', icon: FileText },
  { key: 'sites', label: 'Websites', icon: Globe },
  { key: 'campaigns', label: 'Campaigns', icon: Mail },
  { key: 'booking', label: 'Booking', icon: Calendar },
];

export default function MarketingApp() {
  const [tab, setTab] = useState('templates');
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id;
  const apiFetch = useMemo(() => makeApiFetch(userId), [userId]);

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="w-56 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-100">
          <button onClick={() => navigate('/mail')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-3">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-indigo-600" />
            <h1 className="text-lg font-bold text-gray-900">Marketing</h1>
          </div>
        </div>
        <nav className="flex-1 p-2 space-y-0.5">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${tab === key ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}>
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>
      <div className="flex-1 overflow-auto p-6">
        {tab === 'brand' && <BrandTab userId={userId} />}
        {tab === 'templates' && <TemplatesTab apiFetch={apiFetch} />}
        {tab === 'sites' && <SitesTab apiFetch={apiFetch} />}
        {tab === 'campaigns' && <CampaignsTab apiFetch={apiFetch} />}
        {tab === 'booking' && <BookingTab apiFetch={apiFetch} />}
      </div>
    </div>
  );
}

function BrandTab({ userId }) {
  const brandKey = `jubly_brand_id_${userId || 'default'}`;
  const [brandId, setBrandId] = useState(() => localStorage.getItem(brandKey) || null);
  const [loading, setLoading] = useState(!brandId);

  // The brand persistence backend (pikto.us/api/partner/brands) is not deployed,
  // so we don't probe it on mount — that request 404s and logs on every page
  // load. Rely on a locally-stored brand id instead; when a real brands API
  // exists, restore a guarded list call here.
  useEffect(() => {
    setLoading(false);
  }, [brandId]);

  const client = useMemo(() => {
    if (!brandId) {
      // Return a client that creates a brand on first call
      return new BrandApiClient({
        baseUrl: `${PIKTO_API}/brands`,
        headers: { 'X-Partner-Key': PARTNER_KEY, 'X-Partner-Name': 'jubly' },
        fetchFn: async (path, opts = {}) => {
          const method = opts.method || 'GET';

          // POST to root = create brand
          if (method === 'POST' && (path === '' || path === '/')) {
            const res = await fetch(`${PIKTO_API}/brands`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Partner-Key': PARTNER_KEY },
              body: JSON.stringify({ ...opts.body, creator_email: userId || 'jubly' }),
            });
            const data = await res.json();
            if (data.brand_id || data.brand?.id) {
              const newId = data.brand_id || data.brand.id;
              setBrandId(newId);
              localStorage.setItem(brandKey, newId);
            }
            return data;
          }

          // GET to root = no brand yet
          if (method === 'GET' && (path === '' || path === '/')) {
            return { brand: null };
          }

          return {};
        },
      });
    }

    // Brand exists — use Pikto brand API directly
    return new BrandApiClient({
      baseUrl: `${PIKTO_API}/brands/${brandId}`,
      headers: { 'X-Partner-Key': PARTNER_KEY, 'X-Partner-Name': 'jubly' },
    });
  }, [brandId]);

  if (loading) return <div className="text-gray-400 text-sm p-6">Loading brand...</div>;

  return (
    <div className="-m-6">
      <BrandEditor
        client={client}
        onBrandApplied={(brand) => {
          console.log('Brand applied:', brand);
          if (brand?.brand?.id && !brandId) {
            setBrandId(brand.brand.id);
            localStorage.setItem(brandKey, brand.brand.id);
          }
        }}
        showAssetGeneration={true}
      />
    </div>
  );
}

function TemplatesTab({ apiFetch }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { apiFetch('/templates').then(d => { setTemplates(d.templates || []); setLoading(false); }).catch(() => setLoading(false)); }, []);
  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">Templates</h2>
      {loading ? <p className="text-gray-400">Loading...</p> :
       templates.length === 0 ? <p className="text-gray-400">No templates yet. Generate some from the Brand editor.</p> :
       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map(t => (
          <div key={t.id} className="border rounded-lg overflow-hidden bg-white">
            <div className="aspect-video bg-gray-50 relative overflow-hidden">
              {t.html_content && <iframe srcDoc={t.html_content} className="absolute inset-0 border-0 pointer-events-none origin-top-left" style={{width:'500%',height:'500%',transform:'scale(0.2)'}} sandbox="allow-same-origin allow-scripts" />}
            </div>
            <div className="p-2 flex items-center gap-2">
              <span className="text-xs text-gray-500 truncate flex-1">{t.name}</span>
              <button onClick={() => { apiFetch('/templates/' + t.id, { method: 'DELETE' }).then(() => setTemplates(p => p.filter(x => x.id !== t.id))); }} className="text-red-400 hover:text-red-600">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
       </div>}
    </div>
  );
}

function SitesTab({ apiFetch }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  useEffect(() => { apiFetch('/sites').then(d => { setSites(d.sites || []); setLoading(false); }).catch(() => setLoading(false)); }, []);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">Websites</h2>
        <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-500">
          <Plus className="w-4 h-4" /> New Site
        </button>
      </div>
      {creating && (
        <div className="mb-4 p-4 border rounded-lg space-y-2">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Site name" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <div className="flex gap-2">
            <button onClick={() => { apiFetch('/sites', { method: 'POST', body: { name } }).then(() => { setCreating(false); setName(''); apiFetch('/sites').then(d => setSites(d.sites || [])); }); }} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs">Create</button>
            <button onClick={() => setCreating(false)} className="px-3 py-1.5 border rounded-lg text-xs">Cancel</button>
          </div>
        </div>
      )}
      {loading ? <p className="text-gray-400">Loading...</p> :
       sites.length === 0 ? <p className="text-gray-400">No sites yet.</p> :
       <div className="space-y-2">
        {sites.map(s => (
          <div key={s.id} className="flex items-center gap-3 p-3 border rounded-lg bg-white">
            <Globe className="w-5 h-5 text-gray-400" />
            <div className="flex-1">
              <div className="text-sm font-medium">{s.name}</div>
              <div className="text-xs text-gray-400">{s.domain || 'No domain'} — {s.page_count || 0} pages</div>
            </div>
          </div>
        ))}
       </div>}
    </div>
  );
}

function CampaignsTab({ apiFetch }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { apiFetch('/campaigns').then(d => { setCampaigns(d.campaigns || []); setLoading(false); }).catch(() => setLoading(false)); }, []);
  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">Campaigns</h2>
      {loading ? <p className="text-gray-400">Loading...</p> :
       campaigns.length === 0 ? <p className="text-gray-400">No campaigns yet.</p> :
       <div className="space-y-2">
        {campaigns.map(c => (
          <div key={c.id} className="flex items-center gap-3 p-3 border rounded-lg bg-white">
            <Mail className="w-5 h-5 text-gray-400" />
            <div className="flex-1">
              <div className="text-sm font-medium">{c.name}</div>
              <div className="text-xs text-gray-400">{c.campaign_type} — {c.status}</div>
            </div>
          </div>
        ))}
       </div>}
    </div>
  );
}

function BookingTab({ apiFetch }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [duration, setDuration] = useState(30);
  useEffect(() => { apiFetch('/booking/services').then(d => { setServices(d.services || []); setLoading(false); }).catch(() => setLoading(false)); }, []);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">Booking</h2>
        <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-500">
          <Plus className="w-4 h-4" /> New Service
        </button>
      </div>
      {creating && (
        <div className="mb-4 p-4 border rounded-lg space-y-2">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Service name" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <select value={duration} onChange={e => setDuration(parseInt(e.target.value))} className="px-3 py-2 border rounded-lg text-sm">
            <option value={15}>15 min</option><option value={30}>30 min</option><option value={60}>60 min</option>
          </select>
          <div className="flex gap-2">
            <button onClick={() => { apiFetch('/booking/services', { method: 'POST', body: { name, duration_minutes: duration } }).then(() => { setCreating(false); setName(''); apiFetch('/booking/services').then(d => setServices(d.services || [])); }); }} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs">Create</button>
            <button onClick={() => setCreating(false)} className="px-3 py-1.5 border rounded-lg text-xs">Cancel</button>
          </div>
        </div>
      )}
      {loading ? <p className="text-gray-400">Loading...</p> :
       services.length === 0 ? <p className="text-gray-400">No services yet.</p> :
       <div className="space-y-2">
        {services.map(s => (
          <div key={s.id} className="flex items-center gap-3 p-3 border rounded-lg bg-white">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div className="flex-1">
              <div className="text-sm font-medium">{s.name}</div>
              <div className="text-xs text-gray-400">{s.duration_minutes} min</div>
            </div>
            <button onClick={() => navigator.clipboard.writeText('https://jubly.net/book/' + s.slug)} className="flex items-center gap-1 text-xs text-indigo-600 hover:underline">
              <Copy className="w-3 h-3" /> Copy Link
            </button>
          </div>
        ))}
       </div>}
    </div>
  );
}
